import { useState, useEffect, useRef, useCallback } from 'react';
import { callSNOWDataService } from 'src/utils/snowDataServiceWrapper';
import { SNOW_NXT_WORKFLOW_ENDPOINT } from 'src/config';
import { v4 as uuidv4 } from 'uuid';
import { ListWorkflowDefinitionResonse } from 'src/@types/selfServeTypes/listWorkflowDefinitions.interface';
import { WorkflowDefinition } from 'src/@types/selfServeTypes/workflowDefinition.interface';
import { SessionStorageCache } from './sessionStorageCache';

const CACHE_KEY = 'snow_workflow_cache';
const CACHE_TTL_MS = 2 * 60 * 60 * 1000; // 2 hours

const workflowCache = new SessionStorageCache<WorkflowDefinition[]>(
	CACHE_KEY,
	CACHE_TTL_MS
);

interface UseWorkflowDataResult {
	isLoading: boolean;
	error: string | null;
	allWorkflows: WorkflowDefinition[];
	refetch: () => void;
}

function getCachedWorkflows(): WorkflowDefinition[] | null {
	return workflowCache.get();
}

function setCachedWorkflows(workflows: WorkflowDefinition[]): void {
	workflowCache.set(workflows);
}

export function invalidateWorkflowCache(): void {
	workflowCache.invalidate();
}

export function useWorkflowData(): UseWorkflowDataResult {
	// Read the cache once on mount; get() evicts stale entries.
	const initialCacheRef = useRef<WorkflowDefinition[] | null | undefined>(undefined);
	if (initialCacheRef.current === undefined) {
		initialCacheRef.current = getCachedWorkflows();
	}
	const cachedData = initialCacheRef.current;

	const [allWorkflows, setAllWorkflows] = useState<WorkflowDefinition[]>(cachedData || []);
	const [isLoading, setIsLoading] = useState<boolean>(cachedData === null);
	const [error, setError] = useState<string | null>(null);

	// Bumped by refetch() to force a network fetch.
	const [reloadToken, setReloadToken] = useState(0);

	const shouldFetchOnMount = cachedData === null;

	const refetch = useCallback(() => {
		setIsLoading(true);
		setError(null);
		setReloadToken((token) => token + 1);
	}, []);

	useEffect(() => {
		// Skip the first run when a valid cache was served and no refetch yet.
		if (reloadToken === 0 && !shouldFetchOnMount) {
			return;
		}

		const abortController = new AbortController();

		callSNOWDataService({
			path: '/workflows',
			queryParams: { requestId: uuidv4() },
			method: 'GET',
			url: SNOW_NXT_WORKFLOW_ENDPOINT,
			signal: abortController.signal,
		} as never)
			.then((response: ListWorkflowDefinitionResonse) => {
				const workflows = response?.workflowDefinitions || [];
				setAllWorkflows(workflows);
				setCachedWorkflows(workflows);
				setIsLoading(false);
				setError(null);
			})
			.catch((err) => {
				if (abortController.signal.aborted) return;
				setError(err?.message || 'Failed to fetch workflows');
				setIsLoading(false);
			});

		return () => abortController.abort();
	}, [reloadToken, shouldFetchOnMount]);

	// Auto-refresh on TTL expiry while mounted: eviction is otherwise passive
	// (only checked on the next mount), so a user parked on the page would keep
	// seeing stale data. The effect re-runs when allWorkflows changes after a
	// fetch, rescheduling for the next window.
	useEffect(() => {
		const msUntilExpiry = workflowCache.msUntilExpiry();
		if (msUntilExpiry <= 0) {
			return;
		}
		const timerId = setTimeout(() => refetch(), msUntilExpiry);
		return () => clearTimeout(timerId);
	}, [refetch, allWorkflows]);

	return { isLoading, error, allWorkflows, refetch };
}
